import { Group, Matrix4, Raycaster, Vector2, Vector3 } from 'three';

const _pointer = new Vector2();
const _event = { type: '', data: _pointer };

class InteractiveGroup extends Group {
  private isPointerOver = false;

  private targetObject;

  constructor(renderer, camera, context) {
    super();
    const debug = false;
    let arrowHelper1;
    let arrowHelper2;
    if (context && debug) {
      const dir = new Vector3(1, 0, 0);
      dir.normalize();
      const origin = new Vector3(0, 0, 0);
      const length = 1;
      const hex = 0xffff00;
      arrowHelper1 = new context.three.ArrowHelper(dir, origin, length, hex);
      arrowHelper2 = new context.three.ArrowHelper(dir, origin, length, hex);
      context.scene.add(arrowHelper1);
      context.scene.add(arrowHelper2);
    }

    const raycasterL = new Raycaster();
    const raycasterR = new Raycaster();
    const tempMatrix = new Matrix4();

    const element = renderer.domElement;

    const onPointerEvent = (event) => {
      event.stopPropagation();
      const rect = renderer.domElement.getBoundingClientRect();

      _pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      _pointer.y = (-(event.clientY - rect.top) / rect.height) * 2 + 1;

      raycasterL.setFromCamera(_pointer, camera);
      raycasterR.setFromCamera(_pointer, camera);

      const intersectsL = raycasterL.intersectObjects(this.children, false);
      const intersectsR = raycasterR.intersectObjects(this.children, false);

      if (!intersectsL.length && this.isPointerOver && this.targetObject) {
        this.isPointerOver = false;
        this.targetObject.userData.isPointerOver = this.isPointerOver;
      }

      if (!intersectsR.length && this.isPointerOver && this.targetObject) {
        this.isPointerOver = false;
        this.targetObject.userData.isPointerOver = this.isPointerOver;
      }

      if (intersectsL.length > 0) {
        const intersection = intersectsL[0];

        this.targetObject = intersection.object;
        const { uv } = intersection;

        _event.type = event.type;
        _event.data.set(uv.x, 1 - uv.y);

        if (!this.isPointerOver) {
          this.isPointerOver = true;
          this.targetObject.userData.isPointerOver = this.isPointerOver;
        }

        this.targetObject.dispatchEvent(_event);
      }
      if (intersectsR.length > 0) {
        const intersection = intersectsR[0];

        this.targetObject = intersection.object;
        const { uv } = intersection;

        _event.type = event.type;
        _event.data.set(uv.x, 1 - uv.y);

        if (!this.isPointerOver) {
          this.isPointerOver = true;
          this.targetObject.userData.isPointerOver = this.isPointerOver;
        }

        this.targetObject.dispatchEvent(_event);
      }
    };

    element.addEventListener('pointerdown', onPointerEvent);
    element.addEventListener('pointerup', onPointerEvent);
    element.addEventListener('pointermove', onPointerEvent);
    element.addEventListener('mousedown', onPointerEvent);
    element.addEventListener('mouseup', onPointerEvent);
    element.addEventListener('mousemove', onPointerEvent);
    element.addEventListener('click', onPointerEvent);

    // WebXR Controller Events
    // TODO: Dispatch pointerevents too

    const events = {
      move: 'mousemove',
      select: 'click',
      selectstart: 'mousedown',
      selectend: 'mouseup',
    };

    const controller1 = renderer.xr.getController(0);
    const controller2 = renderer.xr.getController(1);

    const visualiseRaycaster = (arrowHelper, raycaster) => {
      if (!debug) return;
      arrowHelper.setDirection(raycaster.ray.direction);
      arrowHelper.position.set(raycaster.ray.origin.x, raycaster.ray.origin.y, raycaster.ray.origin.z);
    };
    const onXRControllerEvent = (event) => {
      const controller = event.target;
      let raycaster;
      let arrowHelper;
      if (event.target === controller1) {
        raycaster = raycasterR;
        arrowHelper = arrowHelper1;
      }
      if (event.target === controller2) {
        raycaster = raycasterL;
        arrowHelper = arrowHelper2;
      }

      tempMatrix.identity().extractRotation(controller.matrixWorld);

      raycaster.ray.origin.setFromMatrixPosition(controller.matrixWorld);
      raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix);

      if (arrowHelper) visualiseRaycaster(arrowHelper, raycaster);

      const intersections = raycaster.intersectObjects(this.children, false);

      if (intersections.length > 0) {
        const intersection = intersections[0];

        const { object } = intersection;
        const { uv } = intersection;

        _event.type = events[event.type];
        _event.data.set(uv.x, 1 - uv.y);

        object.dispatchEvent(_event);
      }
    };

    controller1.addEventListener('move', onXRControllerEvent);
    controller1.addEventListener('select', onXRControllerEvent);
    controller1.addEventListener('selectstart', onXRControllerEvent);
    controller1.addEventListener('selectend', onXRControllerEvent);
    controller2.addEventListener('move', onXRControllerEvent);
    controller2.addEventListener('select', onXRControllerEvent);
    controller2.addEventListener('selectstart', onXRControllerEvent);
    controller2.addEventListener('selectend', onXRControllerEvent);
  }
}

export { InteractiveGroup };
